// Guarding kore's filter output against the server's narrower operator enums.
//
// kore's URL grammar is deliberately wider than any one facet's declared
// operators, and it never consults them: `filter-url.ts`'s `SUFFIX_OPS` maps
// `_Between` to `Between` for every def that has a picker for it, and
// `negatedOperatorForKind` maps a `!`-prefixed value to `Neq` for `Enum`,
// `Dynamic` and `Number` defs, and to `NotContains` for `Text`. A def's own
// `operators` list constrains the *picker UI* and nothing else. So a
// hand-edited or bookmarked URL can put an operator into `search.rq.filters`
// that this app offers no affordance for and that the server's own op enum
// cannot deserialize — `?locationId=!location-1` against an `IdOp` with only
// `Eq`.
//
// Text facets are the one kind that needs no guard, and it is worth saying
// why rather than leaving it to look like an oversight: `buildFiltersObject`'s
// Text branch emits only `Contains` and `NotContains`, and the server's
// `TextOp` has exactly those two variants. There is nothing a URL can put
// there that the endpoint cannot read, which is why `serviceVersion` is
// forwarded unguarded.
//
// Forwarding one is a sticky 400: the filter row shows a chip, every request
// fails, and the only way out is editing the URL back by hand. Dropping it is
// the lesser wrong — the user sees an unfiltered list rather than a broken
// page, and the chip they cannot have produced through the UI is the one that
// goes.
//
// This is the type-preserving half of the translation both list pages run
// their filters through; `screens-filters.ts`'s `idFilterFromChip` and
// `offsetMinutesFilter` are the shape-changing half.

/**
 * Keep `op` only if the server's own operator union can express it.
 *
 * `accepted` must list exactly the `type` tags of `Wire`, which is what makes
 * the runtime check a sound narrowing — the wire types in `api/types.ts` are
 * tagged unions with no other members.
 */
export function acceptedOp<Wire extends { type: string }>(
    op: { type: string } | undefined,
    accepted: readonly Wire["type"][],
): Wire | undefined {
    if (op === undefined) return undefined;
    return (accepted as readonly string[]).includes(op.type) ? (op as Wire) : undefined;
}
